/**
 * AdminSkills — Main admin page for managing all skills in the system
 * Features: List all skills, create, edit, delete, search/filter by category or name
 */

import { useState, useEffect } from 'preact/hooks';
import { T } from '../styles/tokens';
import { api } from '../api/client';
import { Button, ListSkeleton, SkeletonBar } from '../components/ui';
import { SkillCard } from '../components/SkillCard';
import { SkillCreateDialog } from '../components/SkillCreateDialog';
import { SkillEditDialog } from '../components/SkillEditDialog';

interface Skill {
  id?: string;
  name: string;
  description: string;
  emoji?: string;
  category?: string;
  tags?: string[];
  version?: string;
  author?: string;
  active?: number;
  skill_md_content?: string;
  is_mandatory?: number;
}

export function AdminSkills() {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [categories, setCategories] = useState<string[]>([]);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState('');

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [filterMandatory, setFilterMandatory] = useState(false);
  const [togglingMandatory, setTogglingMandatory] = useState('');

  useEffect(() => {
    loadSkills();
  }, []);

  async function loadSkills() {
    setLoading(true);
    setError('');
    try {
      const response = await api.get<{ skills: Skill[] }>('/skills');
      const loadedSkills = response.skills || [];
      setSkills(loadedSkills);

      // Extract unique categories
      const cats = new Set<string>();
      loadedSkills.forEach((s) => {
        if (s.category) cats.add(s.category);
      });
      setCategories(Array.from(cats).sort());
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar skills');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateSkill(skill: {
    name: string;
    description: string;
    category: string;
    emoji: string;
    skillMarkdown: string;
    tags?: string[];
    author?: string;
  }) {
    setCreating(true);
    setError('');
    try {
      const response = await api.post<{ skill: Skill }>('/skills', skill);
      setSkills([...skills, response.skill]);
      setShowCreateDialog(false);

      // Update categories
      if (skill.category && !categories.includes(skill.category)) {
        setCategories([...categories, skill.category].sort());
      }
    } catch (e: any) {
      setError(e.message || 'Erro ao criar skill');
    } finally {
      setCreating(false);
    }
  }

  async function handleEditSkill(
    skillName: string,
    updates: {
      description?: string;
      category?: string;
      emoji?: string;
      skillMarkdown?: string;
      tags?: string[];
      author?: string;
    }
  ) {
    setSaving(true);
    setError('');
    try {
      const response = await api.put<{ skill: Skill }>(
        `/skills/${skillName}`,
        updates
      );
      setSkills(
        skills.map((s) => (s.name === skillName ? response.skill : s))
      );
      setShowEditDialog(false);
      setEditingSkill(null);

      // Update categories
      if (updates.category && !categories.includes(updates.category)) {
        setCategories([...categories, updates.category].sort());
      }
    } catch (e: any) {
      setError(e.message || 'Erro ao salvar skill');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteSkill(skillName: string) {
    setDeleting(skillName);
    setError('');
    try {
      await api.delete(`/skills/${skillName}`);
      setSkills(skills.filter((s) => s.name !== skillName));
      setDeleteConfirm(null);
    } catch (e: any) {
      setError(e.message || 'Erro ao deletar skill');
    } finally {
      setDeleting('');
    }
  }

  async function handleToggleMandatory(skillName: string, currentValue: number) {
    setTogglingMandatory(skillName);
    try {
      const newVal = currentValue ? false : true;
      await api.put(`/admin/cluster/metrics/mandatory-skills/${skillName}`, { mandatory: newVal });
      setSkills(skills.map((s) => s.name === skillName ? { ...s, is_mandatory: newVal ? 1 : 0 } : s));
    } catch (e: any) {
      setError(e.message || 'Erro ao alterar skill obrigatória');
    } finally {
      setTogglingMandatory('');
    }
  }

  // Filter skills based on search and category
  const filteredSkills = skills.filter((skill) => {
    const matchesSearch =
      !searchQuery ||
      skill.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      skill.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !filterCategory || skill.category === filterCategory;
    const matchesMandatory = !filterMandatory || skill.is_mandatory === 1;
    return matchesSearch && matchesCategory && matchesMandatory;
  });

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ margin: '0 0 8px', fontSize: '28px', fontWeight: '700', color: T.text }}>
 Admin Skills
        </h1>
        <p style={{ margin: '0', fontSize: '14px', color: T.textMuted }}>
          Gerencie todos os skills do sistema
        </p>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: '16px',
            borderRadius: '10px',
            background: T.dangerBg,
            border: `1px solid ${T.dangerBgStrong}`,
            color: T.danger,
            fontSize: '13px',
            marginBottom: '24px',
          }}
        >
 {error}
        </div>
      )}

      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px',
          flexWrap: 'wrap',
        }}
      >
        <Button variant="primary" onClick={() => setShowCreateDialog(true)} disabled={loading}>
          + Criar Novo Skill
        </Button>

        <input
          type="text"
          placeholder="Buscar skills..."
          value={searchQuery}
          onChange={(e: any) => setSearchQuery(e.target.value)}
          disabled={loading}
          style={{
            flex: 1,
            minWidth: '200px',
            padding: '10px 14px',
            borderRadius: '8px',
            border: '1px solid #1e293b',
            background: T.bgOverlay,
            color: T.text,
            fontSize: '13px',
            opacity: loading ? 0.5 : 1,
          }}
        />

        <select
          value={filterCategory}
          onChange={(e: any) => setFilterCategory(e.target.value)}
          disabled={loading}
          style={{
            padding: '10px 14px',
            borderRadius: '8px',
            border: '1px solid #1e293b',
            background: T.bgOverlay,
            color: T.text,
            fontSize: '13px',
            opacity: loading ? 0.5 : 1,
          }}
        >
          <option value="">Todas as categorias</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>

        <Button variant={filterMandatory ? 'secondary' : 'ghost'} onClick={() => setFilterMandatory(!filterMandatory)}>
          Só obrigatórias
        </Button>
      </div>

      {/* Summary */}
      <div
        style={{
          padding: '12px 16px',
          borderRadius: '10px',
          background: T.accentBg,
          border: `1px solid ${T.accentBg}`,
          fontSize: '13px',
          color: T.textMuted,
          marginBottom: '24px',
        }}
      >
        <strong style={{ color: T.text }}>
          {filteredSkills.length}
        </strong>{' '}
        skill{filteredSkills.length !== 1 ? 's' : ''} encontrado
        {filteredSkills.length !== skills.length && (
          <span>
            {' '}
            de{' '}
            <strong style={{ color: T.text }}>
              {skills.length}
            </strong>
          </span>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div
          style={{
            padding: '48px 24px',
            textAlign: 'center',
            color: T.textMuted,
            fontSize: '14px',
          }}
        >
<ListSkeleton rows={3} />
        </div>
      )}

      {/* Skills Grid */}
      {!loading && filteredSkills.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px',
          }}
        >
          {filteredSkills.map((skill) => (
            <div key={skill.name} style={{ position: 'relative' }}>
              <SkillCard
                skill={skill}
                footer={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                    {/* Badge obrigatória */}
                    {skill.is_mandatory === 1 && (
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '4px 10px', borderRadius: '6px',
                        background: `linear-gradient(135deg, ${T.purpleBgStrong}, ${T.accentBg})`,
                        border: `1px solid ${T.purpleBgStrong}`,
                        color: 'rgba(196,167,255,1)', fontSize: '10px', fontWeight: '700',
                        letterSpacing: '0.5px', textTransform: 'uppercase' as const, width: 'fit-content',
                      }}>
                        SETUP OBRIGATÓRIA
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                    <Button variant="secondary" size="xs" style={{ flex: 1 }} disabled={saving || deleting === skill.name}
                      onClick={() => { setEditingSkill(skill); setShowEditDialog(true); }}>
                      Editar
                    </Button>
                    <Button variant="danger" size="xs" style={{ flex: 1 }} disabled={saving || deleting === skill.name}
                      onClick={() => setDeleteConfirm(skill.name)}>
                      {deleting === skill.name ? '' : ''} Deletar
                    </Button>
                    </div>
                    <Button variant={skill.is_mandatory ? 'secondary' : 'ghost'} size="xs" fullWidth
                      disabled={togglingMandatory === skill.name} loading={togglingMandatory === skill.name}
                      onClick={() => handleToggleMandatory(skill.name, skill.is_mandatory || 0)}>
                      {togglingMandatory === skill.name ? '...' : skill.is_mandatory ? 'Remover obrigatória' : 'Marcar obrigatória'}
                    </Button>
                  </div>
                }
              />

              {/* Delete Confirmation */}
              {deleteConfirm === skill.name && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(11, 15, 26, 0.98)',
                    borderRadius: '12px',
                    border: `1px solid ${T.dangerBgStrong}`,
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '12px',
                    zIndex: 10,
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: '28px',
                        marginBottom: '8px',
                      }}
                    >
 
                    </div>
                    <div style={{ fontSize: '12px', color: T.text, fontWeight: '600' }}>
                      Deletar skill?
                    </div>
                    <div
                      style={{
                        fontSize: '11px',
                        color: T.textMuted,
                        marginTop: '4px',
                      }}
                    >
                      Esta ação não pode ser desfeita
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '6px',
                      width: '100%',
                    }}
                  >
                    <Button variant="ghost" size="xs" style={{ flex: 1 }} disabled={deleting === skill.name}
                      onClick={() => setDeleteConfirm(null)}>
                      Cancelar
                    </Button>
                    <Button variant="danger" size="xs" style={{ flex: 1 }} disabled={deleting === skill.name}
                      loading={deleting === skill.name} onClick={() => handleDeleteSkill(skill.name)}>
                      {deleting === skill.name ? 'Deletando...' : 'Deletar'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && filteredSkills.length === 0 && (
        <div
          style={{
            padding: '48px 24px',
            textAlign: 'center',
            borderRadius: '10px',
            background: T.bgHover,
            border: '1px solid rgba(107, 114, 128, 0.15)',
            color: T.textMuted,
            fontSize: '14px',
          }}
        >
 Nenhum skill encontrado
          {(searchQuery || filterCategory) && (
            <>
              <br />
              <Button variant="ghost"
                onClick={() => {
                  setSearchQuery('');
                  setFilterCategory('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: T.primary,
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '8px',
                  textDecoration: 'underline',
                }}
              >
                Limpar filtros
              </Button>
            </>
          )}
        </div>
      )}

      {/* Dialogs */}
      <SkillCreateDialog
        isOpen={showCreateDialog}
        isLoading={creating}
        error={error}
        onClose={() => setShowCreateDialog(false)}
        onSubmit={handleCreateSkill}
      />

      {editingSkill && (
        <SkillEditDialog
          isOpen={showEditDialog}
          skill={editingSkill}
          isLoading={saving}
          error={error}
          onClose={() => {
            setShowEditDialog(false);
            setEditingSkill(null);
          }}
          onSubmit={handleEditSkill}
        />
      )}
    </div>
  );
}
